import { useMemo } from 'react'
import type { JSX, ReactNode } from 'react'

import {
  CollaborationNotesProvider,
  useCollaborationNotesContext,
} from '~publish/components/CollaborationNotes'
import type {
  CollaborationNotesContextValue,
  ComposerDraftStore,
} from '~publish/components/CollaborationNotes'
import { postNoteSubject } from '~publish/hooks/notes/noteSubject'
import type { TeamMemberScope } from '~publish/hooks/notes/selectableTeamMembersQuery'

import { postNotesAdapter } from './postNotesAdapter'

/** The Notes parts read the generic thread state; the name stays for them. */
export const usePostNotesContext: () => CollaborationNotesContextValue =
  useCollaborationNotesContext

/**
 * A Post's Notes thread. A Post thread never closes, so only the account's
 * permission decides whether it takes new Notes.
 */
export function PostNotesProvider({
  postId,
  canCreate,
  focusAfterDelete,
  mentionScope,
  composerDrafts,
  children,
}: {
  postId: string
  canCreate: boolean
  focusAfterDelete: () => void
  mentionScope: TeamMemberScope | null
  /** A host-owned composer draft store; see `CollaborationNotesProvider`. */
  composerDrafts?: ComposerDraftStore
  children: ReactNode
}): JSX.Element {
  const subject = useMemo(() => postNoteSubject(postId), [postId])
  return (
    <CollaborationNotesProvider
      subject={subject}
      adapter={postNotesAdapter}
      canCreate={canCreate}
      isClosed={false}
      focusAfterDelete={focusAfterDelete}
      mentionScope={mentionScope}
      composerDrafts={composerDrafts}
    >
      {children}
    </CollaborationNotesProvider>
  )
}
